'use client';
import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/navigation';
import classNames from 'classnames';
import { useProgress } from '@bprogress/next';

import { t } from '../../i18n';
import s from './TabArea.module.scss';

const TabArea = ({
    label = null,
    items = [],
    className = null,
    modifier = '',
    listClassName = null,
    itemClassName = null,
    itemActiveClassName = null,
    toggleClassName = null,
    linkClassName = null,
    linkTextClassName = null,
    useNextRouter = false,
    isLink = true,
    activeItem = null,
    setActiveItem = () => {},
}) => {
    const id = useId();
    const classes = classNames(s.Root, className, [s[`Root--${modifier}`]]);
    const listClasses = classNames(s.List, listClassName);

    return (
        <nav className={classes} aria-label={label} id={id}>
            <div className={listClasses}>
                {items.map((item, index) => (
                    <Item
                        {...item}
                        navId={`${id}-${index}`}
                        key={index}
                        className={itemClassName}
                        itemActiveClassName={itemActiveClassName}
                        toggleClassName={toggleClassName}
                        linkClassName={linkClassName}
                        linkTextClassName={linkTextClassName}
                        useNextRouter={useNextRouter}
                        isActive={item.isActive}
                        isLink={isLink}
                        setActiveItem={setActiveItem}
                        activeItem={activeItem}
                        buttonId={item.buttonId}
                    />
                ))}
            </div>
        </nav>
    );
};

TabArea.propTypes = {
    label: PropTypes.string,
    items: PropTypes.array.isRequired,
    className: PropTypes.string,
    listClassName: PropTypes.string,
    itemClassName: PropTypes.string,
    itemActiveClassName: PropTypes.string,
    toggleClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    linkTextClassName: PropTypes.string,
    useNextRouter: PropTypes.bool,
    isLink: PropTypes.bool,
    setActiveItem: PropTypes.func,
};

const Item = (item) => {
    const {
        navId = '',
        showToggle = false,
        isExpanded = false,
        toggleExpanded = () => {},
        isActive = false,
        isDisabled = false,
        className = null,
        itemActiveClassName = null,
        toggleClassName = null,
        linkClassName = null,
        linkTextClassName = null,
        useNextRouter = false,
        isLink = true,
        setActiveItem = () => {},
        activeItem = null,
        buttonId,
    } = item;

    const listId = `${navId}-list`;

    const classes = classNames(
        s.Item,
        { [s['Item--Active']]: isActive },
        { [s['Item--Disabled']]: isDisabled },
        className,
        { [itemActiveClassName]: isActive && itemActiveClassName }
    );

    const toggleClasses = classNames(s.Toggle, toggleClassName);

    const buttonClasses = classNames(s.Button, {
        [s['Button--Active']]: activeItem === buttonId,
        [s['Button--Disabled']]: isDisabled,
    });

    const handleItemClick = () => {
        if (!isLink && setActiveItem) {
            setActiveItem(buttonId);
        }
    };

    return (
        <div className={classes}>
            {isLink ? (
                <Link
                    {...item}
                    useNextRouter={useNextRouter}
                    className={linkClassName}
                    linkTextClassName={linkTextClassName}
                    isActive={isActive}
                    isDisabled={isDisabled}
                />
            ) : (
                <button
                    className={buttonClasses}
                    onClick={handleItemClick}
                    disabled={isDisabled}>
                    {item.title}
                </button>
            )}

            {showToggle && (
                <button
                    className={toggleClasses}
                    type="button"
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                    aria-controls={listId}
                    aria-label={t('nav.showChildren')}
                />
            )}
        </div>
    );
};

Item.propTypes = {
    navId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    showToggle: PropTypes.bool,
    isExpanded: PropTypes.bool,
    toggleExpanded: PropTypes.func,
    isActive: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isParentActive: PropTypes.bool,
    classsName: PropTypes.string,
    toggleClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    linkTextClassName: PropTypes.string,
    linkIconClassName: PropTypes.string,
    useNextRouter: PropTypes.bool,
};

const Link = ({
    title = '',
    url = '',
    target = null,
    rel = null,
    attrTitle = null,
    className = null,
    linkTextClassName = null,
    useNextRouter = false,
    isActive = false,
    isDisabled = false,
}) => {
    const { start } = useProgress();
    const router = useRouter();
    const classes = classNames(s.Link, className, {
        [s['Link--Disabled']]: isDisabled,
    });
    const linkTextClasses = classNames(s.LinkText, linkTextClassName);

    const handleNextRouteClick = (url) => (e) => {
        e.preventDefault();
        if (isDisabled) return;

        if (isActive) {
            start();
            window.location.href = url;
        } else {
            router.push(url);
        }
    };

    return (
        <a
            onClick={useNextRouter ? handleNextRouteClick(url) : undefined}
            className={classes}
            href={isDisabled ? undefined : url}
            target={target}
            rel={rel}
            title={attrTitle}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : undefined}>
            {typeof title === 'string' ? (
                <span
                    className={linkTextClasses}
                    dangerouslySetInnerHTML={{ __html: title }}
                />
            ) : (
                <span className={linkTextClasses}>{title}</span>
            )}
        </a>
    );
};

Link.propTypes = {
    title: PropTypes.node.isRequired,
    url: PropTypes.string.isRequired,
    target: PropTypes.string,
    rel: PropTypes.string,
    attrTitle: PropTypes.string,
    className: PropTypes.string,
    linkTextClassName: PropTypes.string,
    useNextRouter: PropTypes.bool,
    isActive: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

export default TabArea;
